import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../utils/apiConfig";
import { isSignedInWithGoogle } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";
import "../styles/weekly-snapshot.css";

type SnapshotItem = {
    id: string;
    title: string;
    start: string;   // ISO from backend
    tag?: string;    // Polaris | Athlete | Staff | Administrator | Google
    source: string; // "polaris" | "task" | "google"
};

function fmtLongDate(iso: string) {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

function badgeClass(tag: string) {
    const k = (tag || "").toLowerCase();
    if (k.includes("polaris")) return "ws-badge ws-badge--polaris";
    if (k.includes("athlete")) return "ws-badge ws-badge--athlete";
    if (k.includes("staff")) return "ws-badge ws-badge--staff";
    if (k.includes("admin")) return "ws-badge ws-badge--admin";
    if (k.includes("google")) return "ws-badge ws-badge--google";
    return "ws-badge";
}

export default function WeeklySnapshot() {
    const { googleConnected } = useAuth();
    const [items, setItems] = useState<SnapshotItem[]>([]);
    const [loading, setLoading] = useState(false);

    const signedInWithGoogle = googleConnected || isSignedInWithGoogle();

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const qs = new URLSearchParams({
                    includeGoogle: String(!!signedInWithGoogle),
                });
                const res = await fetch(`${API_BASE_URL}/api/calendar/daily?${qs}`, {
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include", // Send HTTP-only cookies
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setItems(json || []);
            } catch (e) {
                console.error("Weekly snapshot fetch failed", e);
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [signedInWithGoogle]);

    const visible = useMemo(() => items.slice(0, 8), [items]);

    return (
        <section className="ws-wrap" aria-busy={loading}>
            <h2 className="ws-title">Daily Snapshot</h2>

            <div className="ws-list">
                {visible.length === 0 && !loading && (
                    <div className="ws-empty">No upcoming events today.</div>
                )}

                {visible.map((it) => (
                    <article className="ws-card" key={it.id}>
                        <div className="ws-left">
                            <span className={badgeClass(it.tag || it.source)}>{it.tag || it.source}</span>
                        </div>

                        <div className="ws-mid">
                            <div className="ws-label">Event name</div>
                            <div className="ws-name">{it.title || "Untitled"}</div>
                        </div>

                        <div className="ws-right">
                            <div className="ws-label">Due date</div>
                            <div className="ws-date">{fmtLongDate(it.start)}</div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}